<template>
  <b-card>
    <b-card-body id="customers-card-body">
      <b-button
        variant="primary"
        type="button"
        size="sm"
        class="mb-2"
        @click="createCustomerUser()"
      >
        <feather-icon icon="PlusIcon" size="15" />
        <span>{{ t('Add New User') }}</span>
        <b-tooltip
          triggers="hover"
          target="default-filter-new-btn"
          :title="t('Create Customer User')"
          :delay="{ show: 0, hide: 50 }"
        />
      </b-button>

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.firstname || '-' }}
          </span>
          <span v-else-if="props.column.field === 'type'">
            <b-badge pill :variant="props.row.type === 0 ? 'secondary' : 'primary'">
              {{ props.row.type === 0 ? t('Warehouse') : t('Branch') }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'phone_number'">
            {{ props.row.phone_number || '-' }}
          </span>
          <span v-else-if="props.column.field === 'email'">
            {{ props.row.email || '-' }}
          </span>
          <span v-else-if="props.column.field === 'attention_name'">
            {{ props.row.authorizedName || '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailUser(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="`${props.row.type === 0 ? t('Edit Customers Infos') : ''}`"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-customer-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteCustomerUser(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-customer-button-${props.row.id}`"
                  :title="t('Delete Customer')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        attention_name: '',
        status: '',
        email: '',
        created_at: '',
        updated_at: ''
      },
      refundData: null,
      refundSubmitting: false,
      refundPrice: '',
      refundAllPriceCheckbox: false
    };
  },

  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },

    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Name'),
          field: 'name'
        },
        {
          label: this.$t('Surname'),
          field: 'lastname'
        },
        {
          label: this.$t('Email'),
          field: 'email'
        },
        {
          label: this.$t('Creation Date'),
          field: 'created_at'
        },
        {
          label: this.$t('Updated Date'),
          field: 'updated_at'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async created() {
    this.loadItems();
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      const routerId = this.$route.params.id;

      const requestData = {
        customer: parseInt(routerId),
        ...this.filter
      };

      this.isLoading = true;

      useService
        .getAllUsers(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      // if(requestData.dateFrom && requestData.dateTo && (new Date(requestData.dateFrom) > new Date(requestData.dateTo))) {
      //   this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'))
      //   return false
      // }
    },

    setFilter(data) {
      if (data) {
        this.filterValues = data;
        this.filter.page = 1;
        this.loadItems();
      }
    },

    deleteCustomerUser(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Customer User Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteCustomerUserRequest(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Customer User Was Deleted')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    async deleteCustomerUserRequest(id) {
      return useService.deleteCustomerUser(id);
    },

    detailUser(userId) {
      this.$router.push(`/customers/${this.$route.params.id}/users/${userId}`);
    },

    createCustomerUser() {
      const { id } = this.$route.params;
      this.$router.push(`/customers/${id}/users/create`);
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
